import { provideHttpClient, withInterceptorsFromDi } from '@angular/common/http';
import { importProvidersFrom } from '@angular/core';
import { bootstrapApplication, BrowserModule } from '@angular/platform-browser';
import { provideAnimations } from '@angular/platform-browser/animations';
import { provideRouter, Router, withComponentInputBinding } from '@angular/router';
import { ServiceWorkerModule } from '@angular/service-worker';
import {
  BudgetEffects,
  CharityEffects,
  CustomItemEffects,
  DonationEffects,
  ItemEffects,
  OnboardingEffects,
  PageEffects,
  PartnerEffects,
  provideCoreProviders,
  reducerToken,
  StoreEffects,
  UsersEffects,
  ZoneEffects,
} from '@core';
import { EffectsModule } from '@ngrx/effects';
import { StoreModule } from '@ngrx/store';
import { StoreDevtoolsModule } from '@ngrx/store-devtools';
import { encryptKey } from '@rspl-api';
import { AFTER_LOGIN, AuthenticationService, authRoutes, provideRsplAuthProviders } from '@rspl-auth';
import { provideRsplUIProviders } from '@rspl-ui';
import { provideRsplVersion } from '@rspl-version';
import { LaddaModule } from 'angular2-ladda';
import * as CryptoJS from 'crypto-js';
import { NgxStripeModule } from 'ngx-stripe';

import { AppComponent } from './app/app.component';
import { appRoutes } from './app/app.routes';
import { environment } from './environments/environment';

bootstrapApplication(AppComponent, {
  providers: [
    provideHttpClient(withInterceptorsFromDi()),
    provideCoreProviders(environment),
    provideRsplUIProviders(),
    provideRsplVersion({ currentHash: '{{POST_BUILD_ENTERS_HASH_HERE}}' }),
    provideRouter(localStorage.getItem('rspl-user') ? appRoutes(environment) : authRoutes, withComponentInputBinding()),
    provideRsplAuthProviders(),
    importProvidersFrom(
      BrowserModule,
      ServiceWorkerModule.register('ngsw-worker.js', {
        enabled: environment.production,
      }),
      EffectsModule.forRoot([
        PageEffects,
        ZoneEffects,
        DonationEffects,
        CharityEffects,
        PartnerEffects,
        OnboardingEffects,
        StoreEffects,
        UsersEffects,
        ItemEffects,
        CustomItemEffects,
        BudgetEffects,
      ]),
      StoreModule.forRoot(reducerToken),
      StoreDevtoolsModule.instrument({
        maxAge: 25,
        logOnly: environment.production,
        connectInZone: true,
      }),
      LaddaModule.forRoot({
        style: 'zoom-in',
      }),
      NgxStripeModule.forRoot(environment.stripeKey),
    ),
    provideAnimations(),
  ],
})
  .then((appRef) => {
    const authService = appRef.injector.get(AuthenticationService);
    const router = appRef.injector.get(Router);

    const encrypt = (value: string) => {
      const encrypted = CryptoJS.AES.encrypt(CryptoJS.enc.Utf8.parse(value?.toString()), encryptKey, {
        keySize: 128 / 8,
        iv: encryptKey,
        mode: CryptoJS.mode.CBC,
        padding: CryptoJS.pad.Pkcs7,
      });
      return encrypted?.toString();
    };

    // --- Helper: capture attempted URL if not logged in ---
    const captureUrl = () => {
      const attemptedUrl = window.location.pathname + window.location.search + window.location.hash;
      if (!attemptedUrl.includes('login')) {
        localStorage.setItem(AFTER_LOGIN, environment.production ? encrypt(attemptedUrl) : attemptedUrl);
      }
    };

    // Initialize router config once, then only update on auth state changes
    let isInitialized = false;

    authService.isLoggedIn$.subscribe((isLoggedIn) => {
      const newRoutes = isLoggedIn ? appRoutes(environment) : authRoutes;
      router.resetConfig(newRoutes);

      if (!isLoggedIn) {
        captureUrl();
        if (isInitialized) {
          router.navigateByUrl('/login');
        }
      }

      if (!isInitialized) {
        isInitialized = true;
      }
    });
  })
  .catch((err) => console.error(err));
